/**
 * IndexedDB helpers for WebAuthn biometric credential storage.
 *
 * Provides low-level storage primitives for the biometric database:
 * open/create the database, put/get values by key, and storage key constants.
 *
 * Extracted from webauthn.ts to keep file sizes within the 300-line limit.
 *
 * @module webauthn-storage
 */

/** IndexedDB database name for biometric credential storage. */
export const DB_NAME = 'satsflow-biometric';

/** IndexedDB object store name. */
export const STORE_NAME = 'credentials';

/** Key under which the encrypted PIN blob is stored. */
export const PIN_STORAGE_KEY = 'encrypted-pin';

/** Key under which the CryptoKey is stored. */
export const CRYPTO_KEY_STORAGE_KEY = 'crypto-key';

/** Key under which the credential ID is stored. */
export const CREDENTIAL_ID_KEY = 'credential-id';

/**
 * Open (or create) the IndexedDB database for biometric storage.
 *
 * @returns Promise resolving to the IDBDatabase instance
 */
export function openBiometricDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, 1);

    request.onupgradeneeded = (): void => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME);
      }
    };

    request.onsuccess = (): void => {
      resolve(request.result);
    };

    request.onerror = (): void => {
      reject(new Error('Failed to open biometric database'));
    };
  });
}

/**
 * Store a value in the biometric IndexedDB store.
 *
 * @param db - Open IDBDatabase instance
 * @param key - Storage key
 * @param value - Value to store
 */
export function idbPut(db: IDBDatabase, key: string, value: unknown): Promise<void> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.put(value, key);

    request.onsuccess = (): void => {
      resolve();
    };
    request.onerror = (): void => {
      reject(new Error(`Failed to store ${key}`));
    };
  });
}

/**
 * Retrieve a value from the biometric IndexedDB store.
 *
 * @param db - Open IDBDatabase instance
 * @param key - Storage key
 * @returns The stored value, or undefined if not found
 */
export function idbGet<T>(db: IDBDatabase, key: string): Promise<T | undefined> {
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(key);

    request.onsuccess = (): void => {
      resolve(request.result as T | undefined);
    };
    request.onerror = (): void => {
      reject(new Error(`Failed to retrieve ${key}`));
    };
  });
}
